import React, { useEffect, useRef, useContext } from "react";
import { store } from "../context/store";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { LICENCE_KEY } from "../constants/charts";

am4core.addLicense(LICENCE_KEY);

export default function AmChartDateLine({
  chartId,
  width,
  height,
  chartData,
  graphs,
  isStacked,
  dateKey,
  isStack100,
  onClick
}) {
	
const previousDataRef = useRef("");
  const chart = useRef(null);
  const globalState = useContext(store);

  // First effect: chart setup (runs once)
  useEffect(function setupChart() {
    if (chart.current) {
      return;
    }

    var x = am4core.create(chartId, am4charts.XYChart);
    x.paddingRight = 20;
    x.fontSize = 11;
    x.cursor = new am4charts.XYCursor();

    // Date axis
    var dateAxis = x.xAxes.push(new am4charts.DateAxis());
    dateAxis.baseInterval = { timeUnit: "hour", count: 1 };
    dateAxis.skipEmptyPeriods = true;
    dateAxis.strictMinMax = true;
    dateAxis.dateFormats.setKey("hour", "dd MMM HH:mm");
    dateAxis.dateFormats.setKey("day", "dd MMM HH:mm");
    dateAxis.periodChangeDateFormats.setKey("hour", "dd MMM HH:mm");
    dateAxis.periodChangeDateFormats.setKey("day", "dd MMM HH:mm");
    dateAxis.renderer.minGridDistance = 15;
    dateAxis.renderer.labels.template.fontSize = 9;
    dateAxis.renderer.labels.template.rotation = -90;
    dateAxis.renderer.axisFills.template.fillOpacity = 0.05;

    // Value axis
    var valueAxis = x.yAxes.push(new am4charts.ValueAxis());
    valueAxis.strokeWidth = 0;
    valueAxis.renderer.labels.template.fill = "black";

    if (isStack100) {
      valueAxis.percent = true;
    }

    // Series
    for (var i = 0; i < graphs.length; i++) {
      var g = graphs[i];
      var series = x.series.push(new am4charts.LineSeries());
      series.name = g.title;
      series.dataFields.dateX = dateKey;
      series.dataFields.valueY = g.keyColumn;
      series.tensionX = 0.8;
      series.tooltipText =
        g.title + " ({dateX.formatDate('dd MMM HH:mm')}): {valueY}";
      series.tooltip.label.wrap = true;
      series.tooltip.label.width = 300;
      series.tooltip.label.fill = "black";

      if (g.color) {
        series.fill = g.color;
        series.stroke = g.color;
      }

      if (isStacked) {
        series.fillOpacity = 0.8;
        series.stacked = true;
        series.strokeWidth = 1;
      } else {
        var bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.fillOpacity = 1;
        bullet.strokeWidth = 2;
        bullet.circle.radius = 3;
        bullet.circle.strokeWidth = 2;
        series.strokeWidth = 2;
      }

      if (typeof onClick === "function") {
        series.events.on("hit", function (event) {
          var item = event.target.dataItem;
          if (item && item.dataContext && item.dataContext[dateKey]) {
            onClick(item.dataContext[dateKey]);
          }
        });
      }
    }

    // Legend
    var legend = new am4charts.Legend();
    legend.labels.template.fill = "black";
    legend.useDefaultMarker = true;
    x.legend = legend;

    // Scrollbar
    var scrollbar = new am4core.Scrollbar();
    scrollbar.opacity = 0.2;
    x.scrollbarX = scrollbar;

    // Number formatting
    x.numberFormatter.numberFormat = "#.00a";
    x.numberFormatter.bigNumberPrefixes = [
      { number: 1e3, suffix: "K" },
      { number: 1e6, suffix: "M" },
      { number: 1e9, suffix: "Bn" }
    ];

    chart.current = x;

    return function cleanup() {
      x.dispose();
      chart.current = null;
    };
  }, []); // run once

  // Second effect: update data + recompute axis
  useEffect(

      function updateChartData()
      {

          if(
              !chart.current ||
              !Array.isArray(chartData)
          )
          {
              return;
          }

          var sortedData =
              chartData
                  .slice()
                  .sort(
                      function(a, b)
                      {
                          return new Date(a.date) -
                                 new Date(b.date);
                      }
                  );

				  if(sortedData.length === 0)
				  {
				      chart.current.data = [];

				      var valueAxis =
				          chart.current.yAxes.values[0];

				      valueAxis.min = 0;
				      valueAxis.max = 5;

				      return;
				  }

          var dataSignature =
              JSON.stringify(sortedData);

          if(
              previousDataRef.current ===
              dataSignature
          )
          {
              return;
          }

          previousDataRef.current =
              dataSignature;

          chart.current.data =
              sortedData;

          var maxVal = 0;

          sortedData.forEach(

              function(d)
              {

                  graphs.forEach(

                      function(g)
                      {

                          var val =
                              d[g.keyColumn];

                          if(
                              typeof val === "number" &&
                              val > maxVal
                          )
                          {
                              maxVal = val;
                          }

                      }

                  );

              }

          );

          var valueAxis =
              chart.current.yAxes.values[0];

          valueAxis.min = 0;

          valueAxis.max =
              maxVal > 0
                  ? maxVal * 1.1
                  : 5;

          valueAxis.strictMinMax = true;
          valueAxis.extraMax = 0;

      },

      [chartData]

  );

  return <div id={chartId} style={{ width: width, height: height }}></div>;
}
